import React, { createContext, useCallback, useContext, useState } from 'react'

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Tour stage machine:
 *
 *   sidebar ──► area ──► area_done ──► [user clicks store] ──► store_from_area ──► done
 *           │                      └──► [user skips]           ──────────────────► done
 *           └──► store_direct ────────────────────────────────────────────────────► done
 *
 * 'sidebar'        – spotlight the PP nav button (user not on PP route yet)
 * 'area'           – walk through Area View steps; last step instructs clicking a store
 * 'area_done'      – area tour finished naturally; waiting for user to click a store
 *                    (prevents tour re-triggering if user stays on area view)
 * 'store_from_area'– walk through Store View + back-button step at end
 * 'store_direct'   – walk through Store View (no back-button step, includes header)
 * 'done'           – tour completed; nothing runs
 */
export type PPTourStage = 'sidebar' | 'area' | 'area_done' | 'store_from_area' | 'store_direct' | 'done'

interface IPPTourContext {
  stage: PPTourStage
  setStage: (stage: PPTourStage) => void
  completeTour: () => void
}

// ─── Storage keys ─────────────────────────────────────────────────────────────

const LS_DONE_KEY = 'feature_tour_product_performance_done'
const SS_STAGE_KEY = 'feature_tour_product_performance_stage'

const readStage = (): PPTourStage => {
  if (localStorage.getItem(LS_DONE_KEY)) return 'done'
  return (sessionStorage.getItem(SS_STAGE_KEY) as PPTourStage) || 'sidebar'
}

// ─── Context ──────────────────────────────────────────────────────────────────

const PPTourContext = createContext<IPPTourContext | null>(null)

export const PPTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stage, setStageState] = useState<PPTourStage>(readStage)

  const setStage = useCallback((next: PPTourStage) => {
    sessionStorage.setItem(SS_STAGE_KEY, next)
    setStageState(next)
  }, [])

  const completeTour = useCallback(() => {
    localStorage.setItem(LS_DONE_KEY, 'true')
    sessionStorage.removeItem(SS_STAGE_KEY)
    setStageState('done')
  }, [])

  return <PPTourContext.Provider value={{ stage, setStage, completeTour }}>{children}</PPTourContext.Provider>
}

// No-op fallback used when NavMenu is rendered outside PPTourProvider (e.g. MobileMenu).
// stage='done' ensures no Joyride instance tries to run in that context.
const NO_OP_CONTEXT: IPPTourContext = {
  stage: 'done',
  setStage: () => {},
  completeTour: () => {},
}

export const usePPTour = (): IPPTourContext => {
  const ctx = useContext(PPTourContext)
  return ctx ?? NO_OP_CONTEXT
}
